import { normaliseIpv6 } from '../util/ipv6'

// Drives the address fields built by `InetInput` / `CidrInput`, which come in
// three shapes:
//
//   * IPv4 host or subnet — four octet boxes, plus a prefix box for a `cidr`
//   * IPv6 subnet — one address field, a fixed "/", and a prefix box
//   * IPv6 host — a plain field, normalised on blur
//
// In the first two the boxes carry no `name`: one hidden field does, and this
// keeps it in sync, so the form still submits a single string and nothing on the
// server changes.
//
// A partly filled address is submitted as typed rather than dropped. "192.168.."
// is refused by `IpAddressValidator` with a message naming the field, which is a
// better outcome than silently discarding what someone half-entered.
up.compiler('[data-ip-address]', (element) => {
  const hidden = element.querySelector('[data-ip-value]')
  const octets = Array.from(element.querySelectorAll('[data-ip-octet]'))
  const text = element.querySelector('[data-ip-text]')
  const prefix = element.querySelector('[data-ip-prefix]')
  const prefixMax = prefix ? parseInt(prefix.dataset.ipPrefixMax || '32', 10) : null
  const boxes = (text ? [text] : octets).concat(prefix ? [prefix] : [])

  const clamp = (box) => {
    if (box === text) return box.value

    const max = box === prefix ? prefixMax : 255
    let digits = box.value.replace(/\D/g, '')

    // Typing past the maximum keeps what was valid rather than rewriting it to the
    // maximum: someone typing 1-9-9-9 means 199, not 255.
    while (digits.length && parseInt(digits, 10) > max) {
      digits = digits.slice(0, -1)
    }
    box.value = digits
    return digits
  }

  const compose = () => {
    if (text) {
      if (!text.value.trim().length) return ''
      return prefix && prefix.value.length ? `${text.value}/${prefix.value}` : text.value
    }

    const values = octets.map((box) => box.value)
    if (!values.some((value) => value.length)) return ''

    const address = values.join('.')
    return prefix && prefix.value.length ? `${address}/${prefix.value}` : address
  }

  const sync = () => {
    const next = compose()
    if (hidden.value === next) return

    hidden.value = next
    // `up-validate` and anything else watching the real field listen for these.
    hidden.dispatchEvent(new Event('input', {bubbles: true}))
    hidden.dispatchEvent(new Event('change', {bubbles: true}))
  }

  const distribute = (value) => {
    const raw = String(value).trim()

    if (text) {
      const match = raw.match(/^(.+?)(?:\/(\d{1,3}))?$/)
      if (!match) return false
      text.value = match[1]
      if (prefix) prefix.value = match[2] || ''
      if (prefix) clamp(prefix)
      sync()
      return true
    }

    // Empty groups are allowed. Choosing a network prefills the part of the address
    // it shares — "192.168.2." for a /24, "172.26.." for a /16 — and the boxes have
    // to show the blocks it does carry. Requiring all four left them empty while the
    // hidden field held the prefix, and the first keystroke then composed the boxes
    // over it.
    const match = raw.match(/^(\d{0,3})\.(\d{0,3})\.(\d{0,3})\.(\d{0,3})(?:\/(\d{1,2}))?$/)
    if (!match) return false

    octets.forEach((box, index) => { box.value = match[index + 1] })
    if (prefix) prefix.value = match[5] || ''
    boxes.forEach(clamp)
    sync()
    return true
  }

  const focusNext = (box) => {
    const next = boxes[boxes.indexOf(box) + 1]
    if (next) {
      next.focus()
      next.select()
    }
  }

  const onInput = (event) => {
    const box = event.target
    const digits = clamp(box)
    sync()
    if (box === text) return

    // Advance once the box cannot take another digit — as many characters as the
    // maximum has, or a value no further digit could keep in range.
    const max = box === prefix ? prefixMax : 255
    if (digits.length === String(max).length || parseInt(digits + '0', 10) > max) {
      if (digits.length) focusNext(box)
    }
  }

  const onKeydown = (event) => {
    const box = event.target

    if ((event.key === '.' && box !== text) || (event.key === '/' && box !== prefix)) {
      event.preventDefault()
      if (box.value.length) focusNext(box)
      return
    }

    // Backspace at the start of an empty box steps back, so deleting an address
    // does not need the mouse.
    if (event.key === 'Backspace' && box.value === '' && box.selectionStart === 0) {
      const previous = boxes[boxes.indexOf(box) - 1]
      if (previous) {
        event.preventDefault()
        previous.focus()
        previous.setSelectionRange(previous.value.length, previous.value.length)
      }
    }
  }

  // Technicians paste addresses out of configurations constantly; a field that
  // cannot take one is worse than a plain text box.
  const onPaste = (event) => {
    const pasted = (event.clipboardData || window.clipboardData).getData('text')
    if (distribute(pasted)) {
      event.preventDefault()
    }
  }

  const onBlur = (event) => {
    if (event.target === text) {
      text.value = normaliseIpv6(text.value)
    }
    sync()
  }

  boxes.forEach((box) => {
    box.addEventListener('input', onInput)
    box.addEventListener('keydown', onKeydown)
    box.addEventListener('paste', onPaste)
    box.addEventListener('blur', onBlur)
  })

  distribute(hidden.value)

  return () => {
    boxes.forEach((box) => {
      box.removeEventListener('input', onInput)
      box.removeEventListener('keydown', onKeydown)
      box.removeEventListener('paste', onPaste)
      box.removeEventListener('blur', onBlur)
    })
  }
})

// A plain IPv6 host field. Nothing to keep in sync — it carries its own `name` —
// so all this does is show what will be stored.
up.compiler('input[data-ip-normalise="ipv6"]:not([data-ip-text])', (element) => {
  const onBlur = () => {
    const normalised = normaliseIpv6(element.value)
    if (normalised === element.value) return

    element.value = normalised
    element.dispatchEvent(new Event('change', {bubbles: true}))
  }

  element.addEventListener('blur', onBlur)
  return () => element.removeEventListener('blur', onBlur)
})
